import { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  sanitizePhoneInput,
  validateEmailAddress,
  validatePhoneNumber,
} from '../lib/accountFieldValidation';
import { PHONE_COUNTRY_CODES } from '../lib/phoneCountryCodes';
import { useHomeCountry, withHomeCountry } from '../lib/useHomeCountry';
import { validateReferralCodeOptional } from '../lib/referralCodes';
import { useVehicleTypes } from '../lib/useVehicleTypes';
import { vehicles as vehiclesApi } from '../api/vehicles';
import { CheckIcon, UplIcon } from '../components/driver/driverDocumentIcons';
import LicenceExpiryField from '../components/driver/LicenceExpiryField';
import { registerSoloRider } from '../lib/riderRegistration';
import { startRiderSession } from '../lib/driverSession';
import { rememberFleetRole } from '../lib/fleetRole';
import { refusalMessage } from '../api/errors';
import './driverRegisterPremium.css';

const DOCS = [
  { id: 'nid', label: 'National ID / Passport' },
  { id: 'lic', label: "Driver's License" },
  { id: 'vreg', label: 'Vehicle Registration' },
  { id: 'pv', label: 'Profile Photo with Vehicle' },
];

const STEP_LABELS_SOLO = ['Account type', 'Personal Info', 'Vehicle Info', 'Documents'];
/** Where a rider starts: their details (the account-type step is not shown on InGo). */
const FIRST_STEP = 2;

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M15.5 19.5L8 12l7.5-7.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CamIcon() {
  return (
    <svg viewBox="0 0 24 24" width="32" height="32" fill="none" aria-hidden>
      <rect x="2" y="6" width="20" height="13" rx="1.2" stroke="currentColor" strokeWidth="1.4" fill="none" />
      <path d="M2 8V6a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="12" cy="12.5" r="3" stroke="currentColor" strokeWidth="1.4" fill="none" />
    </svg>
  );
}

function StepCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden>
      <path
        d="M5 12.5l3 2.5 6.5-6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPerson() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconPhone() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6.5 4h3l1.5 4-2 1.2a11 11 0 0 0 5.3 5.3L16.5 13l4 1.5v3a1.5 1.5 0 0 1-1.6 1.5 14 14 0 0 1-12.4-12.4A1.5 1.5 0 0 1 6.5 4Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconEnvelope() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 6h16v12H4V6Zm0 0 8 7 8-7"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconIdCard() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="9" cy="12" r="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M14 10h5M14 14h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M8 11V8a4 4 0 0 1 8 0v3"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconEye({ open }) {
  if (open) {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 3l18 18M10.5 10.5a3 3 0 0 0 3 3M5.2 5.2C3.1 6.4 1.5 8.1 1 9.5c0 0 4 7 11 7 1.2 0 2.3-.2 3.3-.5M8.2 4.2C9.3 3.8 10.6 3.5 12 3.5c7 0 11 6.5 11 6.5-.2.4-1.1 1.6-2.4 2.8"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconVehicle() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 11h14l-1.5-4H6.5L5 11Zm0 0v5h2v-1.5M19 16v-1.5h2V16M7.5 16.5a1.5 1.5 0 1 0 0 .01M16.5 16.5a1.5 1.5 0 1 0 0 .01"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconTag() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 12V6a2 2 0 0 1 2-2h6l8 8-8 8-8-8Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="9" r="1" fill="currentColor" />
    </svg>
  );
}

function StepProgress({ step, labels }) {
  return (
    <div className="dp-reg-progress-track" aria-label={`Registration step ${step} of ${labels.length}`}>
      {labels.map((label, i) => {
        const num = i + 1;
        const isDone = step > num;
        const isActive = step === num;
        const circleClass = isDone ? 'is-done' : isActive ? 'is-active' : 'is-upcoming';
        const labelClass = isDone ? 'is-done' : isActive ? 'is-active' : '';
        return (
          <Fragment key={label}>
            <div className="dp-reg-progress-node">
              <div className={`dp-reg-progress-circle ${circleClass}`}>
                {isDone ? <StepCheckIcon /> : num}
              </div>
              <span className={`dp-reg-progress-label ${labelClass}`}>{label}</span>
            </div>
            {i < labels.length - 1 && (
              <div className={`dp-reg-progress-line ${step > num ? 'is-done' : ''}`} aria-hidden />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

function RegField({ label, htmlFor, icon: Icon, children, error, errorId }) {
  return (
    <div className="dp-reg-field">
      <label className="dp-reg-label" htmlFor={htmlFor}>
        {label}
      </label>
      <div className="dp-reg-input-wrap">
        <span className="dp-reg-iconbox" aria-hidden>
          <Icon />
        </span>
        {children}
      </div>
      {error ? (
        <p className="dp-reg-mismatch" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const initialFiles = () =>
  DOCS.reduce((a, d) => {
    a[d.id] = null;
    return a;
  }, {});

export default function DriverRegisterPage({ vehicleTypes = vehiclesApi } = {}) {
  const navigate = useNavigate();
  // A rider new to InGo who chose Google on the sign-in screen arrives with
  // their name, address and Google's token (ADR 0023 §6): the form starts
  // filled, and the token links Google to the account made.
  const google = useLocation().state?.google || null;
  // "Delivery company" is out of InGo's registration (Rio, 2 Oct 2026: fleets
  // are not quoted), so the account-type step is skipped: a rider starts at
  // their details, shown as step 1. The steps keep their numbers inside.
  const [step, setStep] = useState(FIRST_STEP);
  // Solo only on InGo; the company paths below stay for when fleets are quoted.
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    fullName: [google?.firstName, google?.lastName].filter(Boolean).join(' '),
    phone: '',
    email: google?.email || '',
    nationalId: '',
    password: '',
    confirm: '',
    // The home country InGo's Settings name, once the API says (ADR 0037 there).
    countryIso: '',
    // The key of one of the office's vehicle types, once the list is read.
    vehicleType: '',
    vMake: '',
    vModel: '',
    vPlate: '',
    vColor: '',
    referralCode: '',
    googleIdToken: google?.idToken || '',
    companyName: '',
    tradingName: '',
  });
  /** @type {{ [key: string]: { name: string, file: File } | null }} */
  const [docFiles, setDocFiles] = useState(() => initialFiles());
  /** Headshot step 1 — optional; saved as profile_photo_url */
  const [profilePhoto, setProfilePhoto] = useState(null);
  const fileRefs = useRef({});
  const profilePhotoInputRef = useRef(null);
  /** The day the licence expires: InGo checks it with the licence (ADR 0014). */
  const [licenceExpiry, setLicenceExpiry] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [touched, setTouched] = useState({ email: false, phone: false });
  const [attemptedStep1, setAttemptedStep1] = useState(false);
  // A rider who runs a delivery company registers as any rider, with their own
  // vehicle and papers, and names the company; it opens once they are
  // registered, and they add their riders from Fleet (InGo ADR 0032, F1).
  const [runsCompany, setRunsCompany] = useState(false);

  const stepLabels = STEP_LABELS_SOLO;

  const home = useHomeCountry();

  useEffect(() => {
    setForm((f) => withHomeCountry(f, home));
  }, [home]);

  // The vehicle types the office keeps (Rio, 4 Oct 2026: "not limited to the
  // three kinds we have"); the first is chosen until the rider picks one.
  const { vehicles, loading: vehiclesLoading, failed: vehiclesFailed, retry: retryVehicles } = useVehicleTypes(vehicleTypes);

  useEffect(() => {
    setForm((f) => (vehicles.length === 0 || vehicles.some((v) => v.key === f.vehicleType) ? f : { ...f, vehicleType: vehicles[0].key }));
  }, [vehicles]);

  useEffect(
    () => () => {
      if (profilePhoto?.previewUrl) URL.revokeObjectURL(profilePhoto.previewUrl);
    },
    [profilePhoto?.previewUrl],
  );

  const onChange = (e) => {
    const { name, value } = e.target;
    const next = name === 'phone' ? sanitizePhoneInput(value) : value;
    setForm((f) => ({ ...f, [name]: next }));
  };

  const pick = (id) => {
    fileRefs.current[id]?.click();
  };
  const onFile = (id, e) => {
    const f = e.target.files?.[0];
    if (f) setDocFiles((d) => ({ ...d, [id]: { name: f.name, file: f } }));
  };

  const pickProfilePhoto = () => profilePhotoInputRef.current?.click();

  const onProfilePhotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith('image/')) {
      if (f && !f.type.startsWith('image/')) {
        e.target.value = '';
      }
      return;
    }
    setProfilePhoto((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return { file: f, name: f.name, previewUrl: URL.createObjectURL(f) };
    });
    e.target.value = '';
  };

  const onProfilePhotoKeyDown = (ev) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      pickProfilePhoto();
    }
  };

  const phoneCheck = validatePhoneNumber(form.phone);
  const emailCheck = validateEmailAddress(form.email);
  const showPhoneError = (touched.phone || attemptedStep1) && !phoneCheck.ok;
  const showEmailError = (touched.email || attemptedStep1) && !emailCheck.ok;

  const goToStep2 = () => {
    setErrorMessage('');
    setAttemptedStep1(true);
    if (!phoneCheck.ok || !emailCheck.ok) return;
    if (!form.countryIso) {
      setErrorMessage("Choose your phone number's country.");
      return;
    }
    if (!form.fullName || !form.nationalId || !form.password || form.password !== form.confirm) return;
    if (runsCompany && !form.companyName.trim()) {
      setErrorMessage('Enter your delivery company name.');
      return;
    }
    setStep(3);
  };
  const canNextVehicle = vehicles.some((v) => v.key === form.vehicleType) && form.vMake && form.vModel && form.vPlate && form.vColor;
  const canSubmit = DOCS.every((d) => docFiles[d.id]?.file) && Boolean(licenceExpiry);


  const submitApplication = async () => {
    setErrorMessage('');
    if (!canSubmit) return;
    const phoneCheckSubmit = validatePhoneNumber(form.phone);
    if (!phoneCheckSubmit.ok) {
      setErrorMessage(phoneCheckSubmit.error);
      return;
    }
    const emailCheckSubmit = validateEmailAddress(form.email);
    if (!emailCheckSubmit.ok) {
      setErrorMessage(emailCheckSubmit.error);
      return;
    }
    const referralCheck = validateReferralCodeOptional(form.referralCode);
    if (!referralCheck.ok) {
      setErrorMessage(referralCheck.error);
      return;
    }
    setIsSubmitting(true);

    // A rider registers on InGo's API (the mobile plan's M2): the account,
    // then each document with the rider's own token, and the company when
    // they run one (ADR 0032).
    try {
      const { session, unsent, company } = await registerSoloRider({
        form,
        docFiles,
        licenceExpiry,
        company: runsCompany ? { name: form.companyName, tradingName: form.tradingName } : null,
      });
      startRiderSession(session.account, { rememberMe: true });
      if (company?.opened) rememberFleetRole('owner');
      navigate('/driver/application', {
        replace: true,
        state: { unsent, companyProblem: company && !company.opened ? company.problem : '' },
      });
    } catch (error) {
      setErrorMessage(refusalMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dp-reg-page">
      <header className="dp-reg-nav">
        <button
          type="button"
          className="dp-reg-nav-back"
          onClick={() => (step > FIRST_STEP ? setStep((s) => s - 1) : navigate(-1))}
          aria-label="Back"
        >
          <BackIcon />
        </button>
        <h1 className="dp-reg-nav-title">Driver Registration</h1>
      </header>

      <div className="dp-reg-steps-panel">
        <p className="dp-reg-step-count">
          Step {step - FIRST_STEP + 1} of {stepLabels.length - FIRST_STEP + 1}
        </p>
        <StepProgress step={step - FIRST_STEP + 1} labels={stepLabels.slice(FIRST_STEP - 1)} />
      </div>

      <main className="dp-reg-main">
        <div className="dp-reg-card">
          {step === 2 && (
            <>
              <div className="dp-reg-photo">
                <input
                  ref={profilePhotoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  style={{ display: 'none' }}
                  aria-label="Choose profile photo"
                  onChange={onProfilePhotoChange}
                />
                <div
                  className={
                    profilePhoto?.previewUrl
                      ? 'dp-reg-photo-circle dp-reg-photo-circle--has'
                      : 'dp-reg-photo-circle'
                  }
                  role="button"
                  tabIndex={0}
                  onClick={pickProfilePhoto}
                  onKeyDown={onProfilePhotoKeyDown}
                  aria-label={profilePhoto ? 'Change profile photo' : 'Add profile photo'}
                >
                  {profilePhoto?.previewUrl ? (
                    <img src={profilePhoto.previewUrl} alt="Profile preview" />
                  ) : (
                    <CamIcon />
                  )}
                </div>
                <button type="button" className="dp-reg-photo-btn" onClick={pickProfilePhoto}>
                  {profilePhoto ? 'Change photo' : 'Upload Photo'}
                </button>
                <p className="dp-reg-photo-hint">Tap to upload profile photo</p>
              </div>

              <RegField label="Full Name" htmlFor="dr-n" icon={IconPerson}>
                <input
                  className="dp-reg-input"
                  id="dr-n"
                  name="fullName"
                  value={form.fullName}
                  onChange={onChange}
                  autoComplete="name"
                  placeholder="Full name"
                />
              </RegField>

              <div className="dp-reg-field">
                <label className="dp-reg-label" htmlFor="dr-p">
                  Phone Number
                </label>
                <div className="dp-reg-phone-wrap">
                  <span className="dp-reg-iconbox" aria-hidden>
                    <IconPhone />
                  </span>
                  <select
                    className="dp-reg-phone-cc"
                    name="countryIso"
                    value={form.countryIso}
                    aria-label="Country calling code"
                    onChange={onChange}
                  >
                    {form.countryIso === '' ? <option value="">Country</option> : null}
                    {PHONE_COUNTRY_CODES.map((c) => (
                      <option key={c.iso} value={c.iso}>
                        {c.name} ({c.dial})
                      </option>
                    ))}
                  </select>
                  <input
                    className="dp-reg-phone-input"
                    id="dr-p"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                    type="tel"
                    inputMode="tel"
                    placeholder={home?.phoneExample ?? ''}
                    autoComplete="tel-national"
                    aria-invalid={showPhoneError || undefined}
                    aria-describedby={showPhoneError ? 'dr-phone-error' : undefined}
                  />
                </div>
                {showPhoneError ? (
                  <p className="dp-reg-mismatch" id="dr-phone-error" role="alert">
                    {phoneCheck.error}
                  </p>
                ) : null}
              </div>

              <RegField
                label="Email Address"
                htmlFor="dr-e"
                icon={IconEnvelope}
                error={showEmailError ? emailCheck.error : null}
                errorId="dr-email-error"
              >
                <input
                  className="dp-reg-input"
                  id="dr-e"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  autoComplete="email"
                  placeholder="you@email.com"
                  aria-invalid={showEmailError || undefined}
                  aria-describedby={showEmailError ? 'dr-email-error' : undefined}
                />
              </RegField>

              <RegField label="National ID Number" htmlFor="dr-nid" icon={IconIdCard}>
                <input
                  className="dp-reg-input"
                  id="dr-nid"
                  name="nationalId"
                  value={form.nationalId}
                  onChange={onChange}
                  autoComplete="off"
                  placeholder="National ID number"
                />
              </RegField>

              <RegField label="Password" htmlFor="dr-pw1" icon={IconLock}>
                <input
                  className="dp-reg-input"
                  id="dr-pw1"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={onChange}
                  autoComplete="new-password"
                  placeholder="Create password"
                />
                <button
                  type="button"
                  className="dp-reg-input-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <IconEye open={!showPassword} />
                </button>
              </RegField>

              <RegField label="Confirm Password" htmlFor="dr-pw2" icon={IconLock}>
                <input
                  className="dp-reg-input"
                  id="dr-pw2"
                  name="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  value={form.confirm}
                  onChange={onChange}
                  autoComplete="new-password"
                  placeholder="Confirm password"
                />
                <button
                  type="button"
                  className="dp-reg-input-toggle"
                  onClick={() => setShowConfirm((s) => !s)}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                >
                  <IconEye open={!showConfirm} />
                </button>
              </RegField>

              <RegField label="Referral code (optional)" htmlFor="dr-ref" icon={IconIdCard}>
                <input
                  className="dp-reg-input"
                  id="dr-ref"
                  name="referralCode"
                  value={form.referralCode}
                  onChange={(e) => setForm((f) => ({ ...f, referralCode: e.target.value.toUpperCase() }))}
                  autoComplete="off"
                  placeholder="e.g. INGO-XXXXXX"
                />
              </RegField>

              <label htmlFor="dr-runs-company" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '0.25rem 0 0.75rem', fontWeight: 600 }}>
                <input
                  id="dr-runs-company"
                  type="checkbox"
                  checked={runsCompany}
                  onChange={(e) => setRunsCompany(e.target.checked)}
                />{' '}
                I run a delivery company. I add my riders after, from Fleet.
              </label>

              {runsCompany ? (
                <>
                  <RegField label="Company name" htmlFor="dr-co" icon={IconTag}>
                    <input
                      className="dp-reg-input"
                      id="dr-co"
                      name="companyName"
                      value={form.companyName}
                      onChange={onChange}
                      placeholder="e.g. City Express Deliveries"
                    />
                  </RegField>
                  <RegField label="Trading name (optional)" htmlFor="dr-tr" icon={IconTag}>
                    <input
                      className="dp-reg-input"
                      id="dr-tr"
                      name="tradingName"
                      value={form.tradingName}
                      onChange={onChange}
                      placeholder="Optional public brand name"
                    />
                  </RegField>
                </>
              ) : null}

              {form.password && form.confirm && form.password !== form.confirm && (
                <p className="dp-reg-mismatch">Passwords do not match</p>
              )}

              <button type="button" className="dp-reg-btn-next" onClick={goToStep2}>
                Next
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <RegField label="Vehicle Type" htmlFor="dr-vt" icon={IconVehicle}>
                <select
                  className="dp-reg-input"
                  id="dr-vt"
                  name="vehicleType"
                  value={form.vehicleType}
                  onChange={onChange}
                  disabled={vehicles.length === 0}
                >
                  {vehicles.length === 0 ? <option value="">{vehiclesLoading ? 'Finding InGo’s vehicles…' : 'No vehicles to choose'}</option> : null}
                  {vehicles.map((v) => (
                    <option key={v.key} value={v.key}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </RegField>
              {vehiclesFailed ? (
                <p className="dp-reg-foot" role="alert">
                  InGo&apos;s vehicles could not be loaded.{' '}
                  <button type="button" className="dp-reg-text-btn" onClick={retryVehicles}>
                    Try again
                  </button>
                </p>
              ) : null}

              <RegField label="Vehicle Make" htmlFor="dr-vmk" icon={IconTag}>
                <input
                  className="dp-reg-input"
                  id="dr-vmk"
                  name="vMake"
                  value={form.vMake}
                  onChange={onChange}
                  placeholder="e.g. Honda"
                />
              </RegField>

              <RegField label="Vehicle Model" htmlFor="dr-vmd" icon={IconTag}>
                <input
                  className="dp-reg-input"
                  id="dr-vmd"
                  name="vModel"
                  value={form.vModel}
                  onChange={onChange}
                  placeholder="e.g. 125"
                />
              </RegField>

              <RegField label="Plate Number" htmlFor="dr-vpl" icon={IconTag}>
                <input
                  className="dp-reg-input"
                  id="dr-vpl"
                  name="vPlate"
                  value={form.vPlate}
                  onChange={onChange}
                  placeholder="e.g. ABC-2019"
                />
              </RegField>

              <RegField label="Vehicle Color" htmlFor="dr-vcl" icon={IconTag}>
                <input
                  className="dp-reg-input"
                  id="dr-vcl"
                  name="vColor"
                  value={form.vColor}
                  onChange={onChange}
                  placeholder="e.g. Red"
                />
              </RegField>

              <button type="button" className="dp-reg-btn-next" onClick={() => setStep(4)} disabled={!canNextVehicle}>
                Next
              </button>
            </>
          )}

          {step === 4 && (
            <>
              {errorMessage ? (
                <p className="dp-reg-error" role="alert">
                  {errorMessage}
                </p>
              ) : null}

              <h2 className="dp-reg-docs-title">
                Upload Your Documents
              </h2>

              {DOCS.map((d) => (
                <div key={d.id}>
                  <input
                    ref={(el) => {
                      fileRefs.current[d.id] = el;
                    }}
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: 'none' }}
                    onChange={(e) => onFile(d.id, e)}
                    aria-label={d.label}
                  />
                  <button
                    type="button"
                    className={
                      docFiles[d.id]?.file ? 'dp-reg-doc-btn dp-reg-doc-btn--ok' : 'dp-reg-doc-btn'
                    }
                    onClick={() => pick(d.id)}
                  >
                    {docFiles[d.id]?.file ? <CheckIcon /> : <UplIcon />}
                    <span className="dp-reg-doc-btn-label">{d.label}</span>
                    <span className="dp-reg-doc-btn-hint">Tap to upload</span>
                    {docFiles[d.id] && (
                      <span className="dp-reg-doc-btn-file">{docFiles[d.id].name}</span>
                    )}
                  </button>
                  {d.id === 'lic' && (
                    <LicenceExpiryField value={licenceExpiry} onChange={setLicenceExpiry} />
                  )}
                </div>
              ))}

              <button
                type="button"
                className="dp-reg-btn-next"
                disabled={!canSubmit || isSubmitting}
                onClick={submitApplication}
              >
                {isSubmitting ? 'Submitting…' : 'Submit application'}
              </button>
              <p className="dp-reg-foot">Your application will be reviewed within 24 hours</p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
